//
// Copyright (c) 2011-2021 by Curalate, Inc.
//

import { EditorState, Modifier } from 'draft-js';

import { ISmartInputContext } from '@components/molecules/smart-input/SmartInputContext';
import { SmartInputEntityType } from '@components/molecules/smart-input/smartInputModel';
import {
  getCurrentStateProperties,
  getCharBeforeSelection,
  getCharAfterSelection,
} from '@components/molecules/smart-input/smartInputUtils';

/**
 * Returns a hashtag string transformed based on the character before the selection
 *
 * @param tag hashtag string
 * @param charBefore character before selection
 */
const transformHashtagForBeforeText = (tag: string, charBefore: string) => {
  switch (charBefore) {
    // If character before is a "#" then drop "#" from tag
    case '#':
      return tag.slice(1);

    // If character before is a space or nothing (beginning of input) then do not modify
    case ' ':
    case '':
      return tag;

    // If character before is anything else then add a space before
    default:
      return ` ${tag}`;
  }
};

/**
 * Returns a hashtag string transformed based on the character after the selection
 *
 * @param tag hashtag string
 * @param charAfter character after selection
 */
const transformHashtagForAfterText = (
  tag: string,
  charAfter: string,
): string => {
  switch (charAfter) {
    // If character after is a space then do not add a space to end of tag
    case ' ':
      return tag;

    // If character after is anything else then add a space to end of tag
    default:
      return `${tag} `;
  }
};

/**
 * Returns a hashtag string transformed based on the text before and after the selection
 *
 * @param tag hashtag string
 * @param charBefore character before selection
 * @param charAfter character after selection
 */
const transformHashtagForSurroundingText = (
  tag: string,
  charBefore: string,
  charAfter: string,
) => {
  const transformedTagForBeforeText = transformHashtagForBeforeText(
    tag,
    charBefore,
  );

  const transformedTagForAfterText = transformHashtagForAfterText(
    transformedTagForBeforeText,
    charAfter,
  );

  return transformedTagForAfterText;
};

/**
 * Updates the current editor state with the hashtag added
 *
 * @param tag The hashtag string to be added
 * @param smartInputContext
 */
export const insertHashtag = (
  tag: string,
  smartInputContext: ISmartInputContext,
) => {
  const editorState = smartInputContext.editor.getEditorState();

  const { currentContentState, currentSelectionState } =
    getCurrentStateProperties(editorState);

  const contentStateWithHashtagEntity = currentContentState.createEntity(
    SmartInputEntityType.Hashtag,
    'MUTABLE',
    {
      value: tag,
    },
  );
  const entityKey = contentStateWithHashtagEntity.getLastCreatedEntityKey();

  const charBeforeSelection = getCharBeforeSelection(
    currentContentState,
    currentSelectionState,
  );
  const charAfterSelection = getCharAfterSelection(
    currentContentState,
    currentSelectionState,
  );
  const transformedTag = transformHashtagForSurroundingText(
    tag,
    charBeforeSelection,
    charAfterSelection ?? '',
  );

  /**
   * Replaces the currently selected text with the hashtag text
   */
  const hashtagReplacedContent = Modifier.replaceText(
    currentContentState,
    currentSelectionState,
    transformedTag,
    undefined, // no inline styles necessary
    entityKey,
  );

  const newEditorState = EditorState.push(
    editorState,
    hashtagReplacedContent,
    'insert-fragment',
  );

  smartInputContext.editor.setEditorState(
    EditorState.forceSelection(
      newEditorState,
      hashtagReplacedContent.getSelectionAfter(),
    ),
    true,
  );
};
